import { Button, Input, Select, Tag, message } from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";

const { Option } = Select;
const { TextArea } = Input;

export const MERGE_FIELD_LABELS = {
  student_name: "Student Name",
  admission_no: "Admission No",
  dob: "Date of Birth",
  gender: "Gender",
  class: "Class",
  from_class: "From Class",
  to_class: "To Class",
  academic_year: "Academic Year",
  from_year: "From Year",
  to_year: "To Year",
  parent_name: "Parent / Guardian Name",
  address: "Address",
  district: "District",
  conduct: "Conduct",
  years_known: "Years Known",
  place: "Place",
  date_of_joining: "Date of Joining",
  nationality: "Nationality",
  religion: "Religion",
  mother_tongue: "Mother Tongue",
  category: "Category",
  blood_group: "Blood Group",
  enrollment_year: "Enrollment Year",
  phone: "Phone",
  school_name: "School Name",
  affiliation_no: "Affiliation No",
  principal_name: "Principal Name",
};

const FORM_TYPE_COLOR = {
  transfer_certificate: "blue",
  study_certificate: "green",
  conduct_certificate: "purple",
};

export const DECLARATION_DEFAULTS = {
  study_certificate:
    "This is to certify that Mr./Ms. {{student_name}}, S/o / D/o {{parent_name}}, Admission No. {{admission_no}}, has studied in {{school_name}} from Class {{from_class}} to Class {{to_class}} during the academic years {{from_year}} to {{to_year}}.\n\nThis certificate is issued on the request of the student for official purposes.",
  conduct_certificate:
    "This is to certify that Mr./Ms. {{student_name}}, S/o / D/o {{parent_name}}, Admission No. {{admission_no}}, studied in {{school_name}} from Class {{from_class}} to Class {{to_class}} during the academic years {{from_year}} to {{to_year}}.\n\nDuring the period of his/her study in this institution, his/her conduct and character were found to be {{conduct}}.\n\nThis certificate is issued on the request of the student for official purposes.",
};

// Parse a single line into alternating static/tag segments
export function parseDeclaration(text) {
  const segments = [];
  const regex = /\{\{(\w+)\}\}/g;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) segments.push({ type: "text", value: text.slice(last, match.index) });
    segments.push({ type: "tag", key: match[1] });
    last = match.index + match[0].length;
  }
  if (last < text.length) segments.push({ type: "text", value: text.slice(last) });
  return segments;
}

// Split declaration text into paragraphs → lines → segments
export function parseDeclarationToParagraphs(text) {
  return text.split("\n\n").map((para) =>
    para.split("\n").map((line) => parseDeclaration(line))
  );
}

export function getFieldsFromTemplate(template) {
  if (!template) return [];
  const allFields = new Set();
  (template.sections || []).forEach((s) => {
    if (s.type === "fields_table" && s.config?.fields) {
      s.config.fields.forEach((f) => allFields.add(f.field_name));
    }
    if ((s.type === "declaration" || s.type === "custom_text") && s.config?.text) {
      const matches = s.config.text.match(/\{\{(\w+)\}\}/g) || [];
      matches.forEach((m) => allFields.add(m.replace(/[{}]/g, "")));
    }
    if (s.type === "numbered_qa" && s.config?.items) {
      for (const item of s.config.items) {
        if (item.field_name) allFields.add(item.field_name);
        for (const sub of item.sub_items || []) {
          if (sub.field_name) allFields.add(sub.field_name);
        }
      }
    }
  });
  return [...allFields];
}

function _numToWords(n) {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  if (n < 20) return ones[n];
  if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
  if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + _numToWords(n % 100) : "");
  if (n < 10000) return ones[Math.floor(n / 1000)] + " Thousand" + (n % 1000 ? " " + _numToWords(n % 1000) : "");
  return String(n);
}

function _dateToWords(dateStr) {
  if (!dateStr) return "";
  const d = moment(dateStr);
  if (!d.isValid()) return "";
  const months = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  return `${_numToWords(d.date())} ${months[d.month()]} ${_numToWords(d.year())}`;
}

export function buildDefaultValues(fieldNames, student, schoolData) {
  const vals = {};
  fieldNames.forEach((key) => {
    switch (key) {
      // ── Student identity ──────────────────────────────────────────────────
      case "student_name":
        vals[key] = student ? `${student.first_name || ""} ${student.last_name || ""}`.trim() : "";
        break;
      case "admission_no":
        vals[key] = student?.unique_id || student?.admission_number || "";
        break;
      case "dob":
        vals[key] = student?.dob ? moment(student.dob).format("DD-MMM-YYYY") : "";
        break;
      case "dob_words":
        vals[key] = student?.dob ? _dateToWords(student.dob) : "";
        break;
      case "gender":
        vals[key] = student?.gender || "";
        break;
      case "blood_group":
        vals[key] = student?.blood_group || "";
        break;
      case "address":
        vals[key] = student?.address || "";
        break;
      case "district":
        vals[key] = student?.region || "";
        break;
      // ── AP TC specific ────────────────────────────────────────────────────
      case "nationality_religion": {
        const nat = student?.nationality || "";
        const rel = student?.religion || "";
        vals[key] = [nat, rel].filter(Boolean).join(" / ");
        break;
      }
      case "nationality":
        vals[key] = student?.nationality || "";
        break;
      case "religion":
        vals[key] = student?.religion || "";
        break;
      case "caste_category":
        vals[key] = student?.category || "";
        break;
      case "category":
        vals[key] = student?.category || "";
        break;
      case "mother_tongue":
        vals[key] = student?.mother_tongue || "";
        break;
      case "first_language":
        vals[key] = student?.languages?.[0]?.name || "";
        break;
      case "second_language":
        vals[key] = student?.languages?.[1]?.name || "";
        break;
      case "class":
        vals[key] = student?.class?.name || "";
        break;
      case "class_leaving":
        vals[key] = student?.class?.name || "";
        break;
      case "date_of_admission":
        vals[key] = student?.admission_date
          ? moment(student.admission_date).format("DD-MMM-YYYY") : "";
        break;
      case "date_of_joining":
        vals[key] = student?.admission_date
          ? moment(student.admission_date).format("DD-MMM-YYYY") : "";
        break;
      case "date_tc":
        vals[key] = moment().format("DD-MMM-YYYY");
        break;
      case "date_tc_applied":
        vals[key] = moment().format("DD-MMM-YYYY");
        break;
      // ── School ────────────────────────────────────────────────────────────
      case "school_name":
        vals[key] = schoolData?.name || "";
        break;
      case "affiliation_no":
        vals[key] = schoolData?.affiliation_no || schoolData?.refId || "";
        break;
      case "principal_name":
        vals[key] = schoolData?.principal_name || "";
        break;
      // ── Parent ────────────────────────────────────────────────────────────
      case "parent_name":
        vals[key] = student?.parent
          ? `${student.parent.first_name || ""} ${student.parent.last_name || ""}`.trim() : "";
        break;
      // ── Declaration merge tags ────────────────────────────────────────────
      case "from_class":
      case "to_class":
      case "from_year":
      case "to_year":
      case "years_known":
      case "place":
      case "conduct":
      case "highest_class":
      case "recognition_details":
      case "mother_name":
      case "course_offered":
      case "medium_of_instruction":
      case "class_first_admitted":
      case "fees_paid":
      case "qualified_promotion":
      case "personal_mark_1":
      case "personal_mark_2":
      case "date_left":
      case "attendance":
        vals[key] = "";
        break;
      default:
        vals[key] = "";
    }
  });
  return vals;
}

const CertBuilderIssuance = () => {
  const user = useSelector(selectUser);
  const queryClient = useQueryClient();

  const [step, setStep] = useState(0);
  const [studentId, setStudentId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [declarationMessage, setDeclarationMessage] = useState("");
  const [issueDate, setIssueDate] = useState(moment());
  const [generating, setGenerating] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [linkedRequest, setLinkedRequest] = useState(null);
  const [queueExpanded, setQueueExpanded] = useState(true);
  const [queuePage, setQueuePage] = useState(0);
  const QUEUE_PAGE_SIZE = 6;

  // ─── Data fetching ────────────────────────────────────────────────────────

  const studentDetailQuery = useQuery(
    ["cb-student-detail", studentId],
    () => axios.get(`${serverUrl}/students/${studentId}`, {
      headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
    }).then((r) => r.data),
    { enabled: !!studentId }
  );

  const templatesQuery = useQuery(
    ["cb-templates", user?.school?._id],
    () => axios.get(`${serverUrl}/cert-builder-templates?school=${user?.school?._id}&status=active`, {
      headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
    }).then((r) => r.data),
    { enabled: !!user?.school?._id }
  );

  const approvedRequestsQuery = useQuery(
    ["cb-approved-requests", user?.school?._id],
    () =>
      axios.get(`${serverUrl}/cert-documents/all-requests?school=${user?.school?._id}&request_status=approved`, {
        headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
      }).then((r) => r.data),
    { enabled: !!user?.school?._id }
  );

  // ─── Pre-populate fields when template + student are selected ────────────

  useEffect(() => {
    if (!selectedTemplate) return;
    const declSection = (selectedTemplate.sections || []).find((s) => s.type === "declaration");
    const rawText = declSection?.config?.text || DECLARATION_DEFAULTS[selectedTemplate.form_type] || "";
    const declText = rawText
      .replace(/\n\nPlace\s*:\s*\{\{place\}\}\s*\nDate\s*:\s*\{\{issue_date\}\}\s*$/i, "")
      .trim();
    setDeclarationMessage(declText);

    const fieldNames = getFieldsFromTemplate(selectedTemplate);
    const extraTags = (declText.match(/\{\{(\w+)\}\}/g) || []).map((m) => m.replace(/[{}]/g, ""));
    const allFields = [...new Set([...fieldNames, ...extraTags])];

    const student = studentDetailQuery.data?.result?.data || studentDetailQuery.data;
    const school = user?.school;
    const defaults = buildDefaultValues(allFields, student, school);
    defaults.issue_date = issueDate ? issueDate.format("DD-MMM-YYYY") : "";
    setFieldValues(defaults);
  }, [issueDate, selectedTemplate, studentDetailQuery.data, user?.school]);

  useEffect(() => {
    setFieldValues((prev) => ({ ...prev, issue_date: issueDate ? issueDate.format("DD-MMM-YYYY") : "" }));
  }, [issueDate]);

  // ─── Actions ─────────────────────────────────────────────────────────────

  function selectTemplate(tmpl) {
    setSelectedTemplate(tmpl);
    setGeneratedDoc(null);
    setStep(2);
  }

  async function generate() {
    if (!selectedTemplate || !studentId) return;
    setGenerating(true);
    try {
      const res = await axios.post(
        `${serverUrl}/cert-documents`,
        {
          template: selectedTemplate._id,
          student: studentId,
          school: user?.school?._id,
          form_type: selectedTemplate.form_type,
          field_values: { ...fieldValues, file_number: "auto" },
          declaration_message: declarationMessage,
          issued_at: issueDate ? issueDate.toISOString() : new Date().toISOString(),
          ...(linkedRequest ? { linked_request_id: linkedRequest._id } : {}),
        },
        { headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` } }
      );
      const doc = res.data?.result?.data;
      setGeneratedDoc(doc);
      queryClient.invalidateQueries("cb-documents");
      queryClient.invalidateQueries("cb-approved-requests");
      queryClient.invalidateQueries("cb-parent-requests");
      message.success("Certificate generated and saved!");
      setStep(3);
    } catch (err) {
      message.error("Failed to generate certificate");
    } finally {
      setGenerating(false);
    }
  }

  function reset() {
    setStep(0);
    setStudentId(null);
    setSelectedTemplate(null);
    setFieldValues({});
    setDeclarationMessage("");
    setIssueDate(moment());
    setGeneratedDoc(null);
    setLinkedRequest(null);
  }

  // ─── Render helpers ───────────────────────────────────────────────────────

  function renderQAInput(fieldName, fieldType) {
    if (!fieldName) return null;
    if (fieldType === "yes_no") {
      return (
        <Select
          style={{ width: 160 }}
          value={fieldValues[fieldName] || undefined}
          onChange={(v) => setFieldValues((prev) => ({ ...prev, [fieldName]: v }))}
          placeholder="Select Yes / No"
          getPopupContainer={(t) => t.parentNode}
        >
          <Option value="Yes">Yes</Option>
          <Option value="No">No</Option>
        </Select>
      );
    }
    if (fieldType === "date") {
      const parsed = fieldValues[fieldName]
        ? moment(fieldValues[fieldName], ["DD.MM.YYYY", "DD-MMM-YYYY", "YYYY-MM-DD"], true)
        : null;
      return (
        <DatePicker
          format="DD.MM.YYYY"
          value={parsed && parsed.isValid() ? parsed : null}
          onChange={(d) => setFieldValues((prev) => ({ ...prev, [fieldName]: d ? d.format("DD.MM.YYYY") : "" }))}
          style={{ width: 200 }}
        />
      );
    }
    if (fieldType === "multiline") {
      return (
        <TextArea
          rows={2}
          value={fieldValues[fieldName] || ""}
          onChange={(e) => setFieldValues((prev) => ({ ...prev, [fieldName]: e.target.value }))}
          style={{ maxWidth: 480 }}
        />
      );
    }
    return (
      <Input
        value={fieldValues[fieldName] || ""}
        onChange={(e) => setFieldValues((prev) => ({ ...prev, [fieldName]: e.target.value }))}
        style={{ maxWidth: 480 }}
      />
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  const allTemplates = templatesQuery.data?.result?.data || [];
  const templates = linkedRequest
    ? allTemplates.filter((t) => t.form_type === linkedRequest.form_type)
    : allTemplates;
  const fieldsToShow = selectedTemplate ? getFieldsFromTemplate(selectedTemplate) : [];

  return (
    <div className="mt-8">
      <div
        style={{ borderRadius: "10px", color: "#616161", padding: "48px 56px" }}
        className="bg-white drop-shadow-md w-full"
      >
        <div className="text-2xl text-orange-500 font-semibold" style={{ marginBottom: 24 }}>
          Generate Certificates
        </div>

        {/* ── Approved Requests Queue ────────────────────────────────────────── */}
        {(() => {
          const approvedReqs = approvedRequestsQuery.data?.result?.data || [];
          const totalPages = Math.ceil(approvedReqs.length / QUEUE_PAGE_SIZE);
          const safePage = Math.min(queuePage, Math.max(0, totalPages - 1));
          const pageReqs = approvedReqs.slice(safePage * QUEUE_PAGE_SIZE, (safePage + 1) * QUEUE_PAGE_SIZE);
          const FORM_LABEL = {
            transfer_certificate: "Transfer Certificate",
            study_certificate: "Study Certificate",
            conduct_certificate: "Conduct Certificate",
          };
          const FORM_COLOR = {
            transfer_certificate: "blue",
            study_certificate: "green",
            conduct_certificate: "purple",
          };
          return (
            <div style={{ marginBottom: 28, border: "1.5px solid #fed7aa", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
              {/* Header bar */}
              <div
                onClick={() => setQueueExpanded((x) => !x)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "14px 20px", background: "#fff8f0", cursor: "pointer",
                  borderBottom: queueExpanded ? "1.5px solid #fed7aa" : "none",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#ea580c" }}>
                    Approved Requests Pending Generation
                  </span>
                  {/* Total count badge */}
                  <span style={{
                    background: approvedReqs.length > 0 ? "#f97316" : "#d1d5db",
                    color: "#fff", borderRadius: 12, padding: "2px 10px",
                    fontSize: 12, fontWeight: 700, minWidth: 24, textAlign: "center",
                  }}>
                    {approvedRequestsQuery.isLoading ? "…" : approvedReqs.length}
                  </span>
                  {/* Active processing indicator */}
                  {linkedRequest && (
                    <span style={{
                      background: "#fef3c7", color: "#b45309", border: "1px solid #fbbf24",
                      borderRadius: 10, padding: "2px 10px", fontSize: 11, fontWeight: 700,
                    }}>
                      Processing #{approvedReqs.findIndex((r) => r._id === linkedRequest._id) + 1}
                    </span>
                  )}
                </div>
                <span style={{ color: "#f97316", fontSize: 16 }}>{queueExpanded ? "▲" : "▼"}</span>
              </div>

              {queueExpanded && (
                <div style={{ padding: "0 20px 16px" }}>
                  {approvedReqs.length === 0 ? (
                    <div style={{ color: "#9ca3af", fontSize: 13, padding: "18px 0", textAlign: "center" }}>
                      {approvedRequestsQuery.isLoading ? "Loading…" : "No approved requests waiting for generation"}
                    </div>
                  ) : (
                    <>
                      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14 }}>
                        <thead>
                          <tr style={{ borderBottom: "1.5px solid #f3f4f6" }}>
                            {["#", "Student", "Certificate Type", "Requested On", "Source", "Action"].map((h) => (
                              <th key={h} style={{ textAlign: "left", fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", padding: "6px 10px 10px" }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pageReqs.map((r, idx) => {
                            const globalIdx = safePage * QUEUE_PAGE_SIZE + idx;
                            const isActive = linkedRequest?._id === r._id;
                            return (
                              <tr key={r._id} style={{
                                borderBottom: "1px solid #f9fafb",
                                background: isActive ? "#fffbeb" : "transparent",
                              }}>
                                {/* Row number */}
                                <td style={{ padding: "10px 10px", fontSize: 12, color: "#9ca3af", fontWeight: 600, width: 36 }}>
                                  {isActive
                                    ? <span style={{ color: "#f97316", fontWeight: 700 }}>▶</span>
                                    : `#${globalIdx + 1}`}
                                </td>
                                <td style={{ padding: "10px 10px" }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}>
                                    {r.student ? `${r.student.first_name} ${r.student.last_name}` : "—"}
                                  </div>
                                  {r.student?.unique_id && (
                                    <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.student.unique_id}</div>
                                  )}
                                </td>
                                <td style={{ padding: "10px 10px" }}>
                                  <Tag color={FORM_COLOR[r.form_type] || "default"} style={{ fontSize: 12 }}>{FORM_LABEL[r.form_type] || r.form_type}</Tag>
                                </td>
                                <td style={{ padding: "10px 10px", fontSize: 12, color: "#6b7280" }}>
                                  {r.createdAt ? moment(r.createdAt).format("DD-MMM-YYYY") : "—"}
                                </td>
                                <td style={{ padding: "10px 10px" }}>
                                  <Tag color={r.request_source === "offline" ? "purple" : "cyan"} style={{ fontSize: 11 }}>
                                    {r.request_source === "offline" ? "Offline" : "Online"}
                                  </Tag>
                                </td>
                                <td style={{ padding: "10px 10px" }}>
                                  {isActive ? (
                                    <span style={{ fontSize: 12, color: "#f97316", fontWeight: 600 }}>In Progress</span>
                                  ) : (
                                    <Button
                                      type="primary"
                                      size="small"
                                      style={{ background: "#f97316", borderColor: "#f97316" }}
                                      onClick={() => {
                                        setLinkedRequest(r);
                                        setStudentId(r.student?._id || null);
                                        setSelectedTemplate(null);
                                        setGeneratedDoc(null);
                                        setFieldValues({});
                                        setDeclarationMessage("");
                                        setIssueDate(moment());
                                        setStep(1);
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                      }}
                                    >
                                      Process
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {/* Pagination footer */}
                      {totalPages > 1 && (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, paddingTop: 10, borderTop: "1px solid #f3f4f6" }}>
                          <span style={{ fontSize: 12, color: "#6b7280" }}>
                            Showing {safePage * QUEUE_PAGE_SIZE + 1}–{Math.min((safePage + 1) * QUEUE_PAGE_SIZE, approvedReqs.length)} of {approvedReqs.length} requests
                          </span>
                          <div style={{ display: "flex", gap: 8 }}>
                            <Button
                              size="small"
                              disabled={safePage === 0}
                              onClick={() => setQueuePage((p) => Math.max(0, p - 1))}
                            >
                              ← Prev
                            </Button>
                            {Array.from({ length: totalPages }, (_, i) => (
                              <Button
                                key={i}
                                size="small"
                                type={i === safePage ? "primary" : "default"}
                                style={i === safePage ? { background: "#f97316", borderColor: "#f97316" } : {}}
                                onClick={() => setQueuePage(i)}
                              >
                                {i + 1}
                              </Button>
                            ))}
                            <Button
                              size="small"
                              disabled={safePage >= totalPages - 1}
                              onClick={() => setQueuePage((p) => Math.min(totalPages - 1, p + 1))}
                            >
                              Next →
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Progress steps — only when a request is being processed */}
        {step > 0 && (
        <div style={{ display: "flex", alignItems: "center", marginBottom: 36 }}>
          {["Select Template", "Fill & Review", "Done"].map((label, i) => {
            const isActive = step === i + 1;
            const isDone = step > i + 1;
            const circleBg = isActive ? "#f97316" : isDone ? "#16a34a" : "#f3f4f6";
            const circleColor = isActive || isDone ? "#fff" : "#9ca3af";
            const labelColor = isActive ? "#f97316" : isDone ? "#16a34a" : "#9ca3af";
            const labelWeight = isActive ? 700 : isDone ? 600 : 500;
            return (
              <React.Fragment key={i}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: "50%",
                    background: circleBg,
                    color: circleColor,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 700, flexShrink: 0,
                    boxShadow: isActive ? "0 0 0 4px rgba(249,115,22,0.18)" : "none",
                    transition: "all 0.2s",
                  }}>
                    {isDone ? "✓" : i + 1}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: labelWeight, color: labelColor, whiteSpace: "nowrap" }}>
                    {label}
                  </span>
                </div>
                {i < 2 && (
                  <div style={{
                    flex: 1, height: 2, margin: "0 16px",
                    background: isDone ? "#16a34a" : "#e5e7eb",
                    transition: "background 0.3s",
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        )}

        {/* Idle placeholder — shown when no request is being processed */}
        {step === 0 && (
          <div style={{
            textAlign: "center", padding: "48px 24px",
            background: "#f9fafb", borderRadius: 12,
            border: "1.5px dashed #e5e7eb", marginBottom: 24,
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
              No Request Selected
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7 }}>
              Click <strong>Process</strong> on any approved request in the queue above to begin generating a certificate.
            </div>
          </div>
        )}

        {/* Linked Request Banner — shown whenever processing a queued request */}
        {linkedRequest && (
          <div style={{
            display: "flex", alignItems: "center", gap: 16,
            background: "linear-gradient(90deg, #fffbeb 0%, #fff8f0 100%)",
            border: "1.5px solid #fbbf24", borderRadius: 12,
            padding: "14px 20px", marginBottom: 24,
          }}>
            <div style={{ fontSize: 22, flexShrink: 0 }}>🔒</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#b45309", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>
                Processing Queued Request
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937" }}>
                {linkedRequest.student ? `${linkedRequest.student.first_name} ${linkedRequest.student.last_name}` : "—"}
                <span style={{ marginLeft: 10 }}>
                  <Tag color={FORM_TYPE_COLOR[linkedRequest.form_type] || "default"} style={{ fontSize: 11 }}>
                    {({ transfer_certificate: "Transfer Certificate", study_certificate: "Study Certificate", conduct_certificate: "Conduct Certificate" })[linkedRequest.form_type] || linkedRequest.form_type}
                  </Tag>
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#92400e", marginTop: 2 }}>
                Student and certificate type are locked to this request. Only matching templates are shown.
              </div>
            </div>
            <Button
              size="small"
              onClick={reset}
              style={{ flexShrink: 0, color: "#6b7280", borderColor: "#d1d5db" }}
            >
              Cancel & Start Fresh
            </Button>
          </div>
        )}

        {/* Step 1 — Select Template */}
        {step === 1 && (
          <div>
            <div className="text-base font-semibold text-gray-500" style={{ marginBottom: 28 }}>
              {linkedRequest
                ? `Templates for ${({ transfer_certificate: "Transfer Certificate", study_certificate: "Study Certificate", conduct_certificate: "Conduct Certificate" })[linkedRequest.form_type] || linkedRequest.form_type} only`
                : "Choose a certificate template to issue"}
            </div>
            {templates.length === 0 ? (
              <div
                style={{ borderRadius: "10px", padding: "48px 24px", marginBottom: 36 }}
                className="bg-gray-50 text-gray-400 text-sm text-center"
              >
                {linkedRequest
                  ? <>No active templates found for <strong>{linkedRequest.form_type.replace(/_/g, " ")}</strong>. Go to <strong>Template Designer</strong> tab to create one.</>
                  : <>No active certificate templates found. Go to <strong>Template Designer</strong> tab to create one first.</>}
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 36 }}>
                {templates.map((tmpl) => (
                  <div
                    key={tmpl._id}
                    onClick={() => selectTemplate(tmpl)}
                    style={{
                      width: 260,
                      borderRadius: 12,
                      background: "#fff",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                      border: "1px solid #f0f0f0",
                      borderTop: "4px solid #f97316",
                      padding: "28px 24px 24px",
                      cursor: "pointer",
                      transition: "box-shadow 0.2s, transform 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "0 8px 28px rgba(249,115,22,0.18)";
                      e.currentTarget.style.transform = "translateY(-3px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <div style={{ marginBottom: 16 }}>
                      <Tag color={FORM_TYPE_COLOR[tmpl.form_type] || "default"} style={{ textTransform: "capitalize" }}>
                        {tmpl.form_type.replace(/_/g, " ")}
                      </Tag>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#1f2937", marginBottom: 10, lineHeight: 1.4 }}>
                      {tmpl.name}
                    </div>
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>
                      {(tmpl.sections || []).length} section{(tmpl.sections || []).length !== 1 ? "s" : ""}
                    </div>
                    <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #f3f4f6", fontSize: 12, color: "#f97316", fontWeight: 600 }}>
                      Select →
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2 — Fill & Review */}
        {step === 2 && selectedTemplate && (() => {
          const isInlineType = ["study_certificate", "conduct_certificate"].includes(selectedTemplate.form_type);
          const declTagKeys = isInlineType && declarationMessage
            ? [...(declarationMessage.match(/\{\{(\w+)\}\}/g) || [])].map((m) => m.replace(/[{}]/g, ""))
            : [];
          const parsedParagraphs = isInlineType && declarationMessage
            ? parseDeclarationToParagraphs(declarationMessage)
            : null;

          const numberedQASections = (selectedTemplate.sections || []).filter((s) => s.type === "numbered_qa");
          const numberedQAFieldKeys = new Set();
          numberedQASections.forEach((s) => {
            (s.config?.items || []).forEach((item) => {
              if (item.field_name) numberedQAFieldKeys.add(item.field_name);
              (item.sub_items || []).forEach((sub) => { if (sub.field_name) numberedQAFieldKeys.add(sub.field_name); });
            });
          });

          const gridFields = fieldsToShow.filter((k) => !declTagKeys.includes(k) && !numberedQAFieldKeys.has(k));

          return (
            <div>
              {/* Template badge */}
              <div style={{
                display: "flex", alignItems: "center", gap: 18,
                background: "linear-gradient(135deg, #fff8f0 0%, #ffffff 100%)",
                border: "1.5px solid #fed7aa", borderRadius: 14,
                padding: "20px 28px", marginBottom: 36,
                boxShadow: "0 2px 10px rgba(249,115,22,0.08)",
              }}>
                <div style={{ fontSize: 36, flexShrink: 0 }}>📄</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#f97316", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>
                    {selectedTemplate.form_type.replace(/_/g, " ")}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#1f2937" }}>{selectedTemplate.name}</div>
                  {linkedRequest?.student && (
                    <div style={{ fontSize: 12, color: "#b45309", marginTop: 6, fontWeight: 600 }}>
                      🔒 Issuing for: {linkedRequest.student.first_name} {linkedRequest.student.last_name}
                    </div>
                  )}
                </div>
                <Tag color="green" style={{ fontSize: 12, padding: "3px 12px" }}>Active Template</Tag>
              </div>

              {/* Inline fill-in-the-blanks for study/conduct certs */}
              {isInlineType && parsedParagraphs && (
                <div
                  style={{ borderRadius: "8px", borderLeft: "4px solid #f97316" }}
                  className="mb-8 p-8 bg-gray-50"
                >
                  <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-5">
                    Certificate Text
                  </div>
                  <div className="text-base text-gray-800">
                    {parsedParagraphs.map((lines, pi) => (
                      <div key={pi} className="mb-6">
                        {lines.map((segs, li) => (
                          <div key={li} className="flex flex-wrap items-baseline" style={{ lineHeight: "2.6" }}>
                            {segs.map((seg, si) =>
                              seg.type === "text" ? (
                                <span key={si}>{seg.value}</span>
                              ) : (
                                <input
                                  key={si}
                                  value={fieldValues[seg.key] || ""}
                                  onChange={(e) => setFieldValues({ ...fieldValues, [seg.key]: e.target.value })}
                                  placeholder={MERGE_FIELD_LABELS[seg.key] || seg.key}
                                  style={{
                                    display: "inline",
                                    border: "none",
                                    borderBottom: "2px solid #f97316",
                                    background: "transparent",
                                    outline: "none",
                                    minWidth: "90px",
                                    width: `${Math.max((fieldValues[seg.key] || MERGE_FIELD_LABELS[seg.key] || seg.key).length + 2, 9)}ch`,
                                    color: "#c2410c",
                                    fontWeight: "bold",
                                    fontSize: "inherit",
                                    fontFamily: "inherit",
                                    padding: "0 6px",
                                    textAlign: "center",
                                  }}
                                />
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Numbered Q&A sections */}
              {numberedQASections.map((section, si) => (
                <div key={si} style={{ marginBottom: 40 }}>
                  {/* Section heading with rule */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap" }}>
                      Certificate Details
                    </span>
                    <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
                  </div>

                  {(section.config?.items || []).map((item, ii) => (
                    <div key={ii} style={{
                      background: "#fff",
                      border: "1.5px solid #e5e7eb",
                      borderRadius: 12,
                      padding: "18px 22px",
                      marginBottom: 14,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                        {/* Orange number badge */}
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%",
                          background: "#fff8f0", border: "1.5px solid #fed7aa",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, fontWeight: 700, color: "#f97316", flexShrink: 0, marginTop: 2,
                        }}>
                          {item.number}
                        </div>

                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 10, lineHeight: 1.5 }}>
                            {item.question}
                          </div>
                          {renderQAInput(item.field_name, item.field_type)}

                          {/* Sub-items */}
                          {(item.sub_items || []).length > 0 && (
                            <div style={{ marginTop: 16, paddingLeft: 16, borderLeft: "2.5px solid #fed7aa", display: "flex", flexDirection: "column", gap: 14 }}>
                              {(item.sub_items || []).map((sub, sii) => (
                                <div key={sii} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                                  <span style={{ fontSize: 11, color: "#f97316", fontWeight: 700, minWidth: 48, paddingTop: 8, flexShrink: 0 }}>
                                    {sub.marker}
                                  </span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 500, marginBottom: 7, lineHeight: 1.4 }}>
                                      {sub.question}
                                    </div>
                                    {renderQAInput(sub.field_name, sub.field_type)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* Grid for remaining fields */}
              {gridFields.length > 0 && (
                <div className="grid grid-cols-2" style={{ columnGap: 48, rowGap: 32, marginBottom: 40 }}>
                  {gridFields.map((key) => (
                    <div key={key} className="flex flex-col" style={{ gap: 10 }}>
                      <h3 className="text-base font-semibold text-gray-600">
                        {MERGE_FIELD_LABELS[key] || key}
                      </h3>
                      <Input
                        size="large"
                        value={fieldValues[key] || ""}
                        onChange={(e) => setFieldValues({ ...fieldValues, [key]: e.target.value })}
                        placeholder={`Enter ${MERGE_FIELD_LABELS[key] || key}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Issue date */}
              <div className="flex flex-col" style={{ gap: 10, marginBottom: 40 }}>
                <h3 className="text-base font-semibold text-gray-600">Issue Date</h3>
                <DatePicker
                  value={issueDate}
                  onChange={(d) => setIssueDate(d)}
                  format="DD-MMM-YYYY"
                  size="large"
                  style={{ width: 260 }}
                  disabledDate={(d) => d && d > moment()}
                />
              </div>

              {/* Declaration textarea for non-inline types */}
              {!isInlineType && (
                <div className="flex flex-col" style={{ gap: 10, marginBottom: 40 }}>
                  <h3 className="text-base font-semibold text-gray-600">Declaration Message</h3>
                  <TextArea
                    rows={5}
                    value={declarationMessage}
                    onChange={(e) => setDeclarationMessage(e.target.value)}
                  />
                </div>
              )}

              <div className="flex gap-4" style={{ paddingTop: 16 }}>
                <Button size="large" onClick={() => setStep(1)} style={{ paddingLeft: 28, paddingRight: 28 }}>
                  ← Back
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={generate}
                  loading={generating}
                  style={{ background: "#f97316", borderColor: "#f97316", paddingLeft: 36, paddingRight: 36 }}
                >
                  Generate Certificate PDF
                </Button>
              </div>
            </div>
          );
        })()}

        {/* Step 3 — Done */}
        {step === 3 && generatedDoc && (
          <div>
            {/* Success banner */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 36 }}>
              <div style={{
                width: 48, height: 48, borderRadius: "50%", background: "#dcfce7",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 22, flexShrink: 0,
              }}>
                ✓
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#15803d" }}>Certificate Generated Successfully</div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                  The certificate has been saved and is ready to download.
                </div>
              </div>
            </div>

            {/* Detail card */}
            <div style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
              padding: "28px 32px",
              marginBottom: 40,
              display: "inline-flex",
              gap: 48,
              flexWrap: "wrap",
            }}>
              {[
                { label: "File No", value: generatedDoc.file_number },
                { label: "Certificate Type", value: selectedTemplate?.form_type?.replace(/_/g, " ") },
                { label: "Template", value: selectedTemplate?.name },
                { label: "Status", value: null, tag: <Tag color="gold" style={{ fontSize: 13 }}>Draft</Tag> },
              ].map((item, i) => (
                <div key={i}>
                  <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                    {item.label}
                  </div>
                  {item.tag
                    ? item.tag
                    : <div style={{ fontSize: 15, fontWeight: 600, color: "#1f2937", textTransform: "capitalize" }}>{item.value || "—"}</div>
                  }
                </div>
              ))}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 16 }}>
              <a href={`${serverUrl}/cert-documents/${generatedDoc._id}/download`} target="_blank" rel="noreferrer">
                <Button
                  type="primary"
                  size="large"
                  style={{ background: "#f97316", borderColor: "#f97316", paddingLeft: 36, paddingRight: 36 }}
                >
                  Download PDF
                </Button>
              </a>
              <Button size="large" onClick={reset} style={{ paddingLeft: 28, paddingRight: 28 }}>
                Issue Another Certificate
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CertBuilderIssuance;
